import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import styles from './_indirect-stips-editor.module.scss';
import { GenericGrid, hasValue, LoadingPanel } from '@amsanalytics/react-shared-library';
import { IndirectDecisionReason, ApplicationStipulation } from './IndirectStipulation';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { associateDocumentWithStipulations, createDecisionDenialReasons, deleteAppCustomerStipDocumentAssociation, deleteDecisionDenialReason, loadIndirectStipsAndDecisionReasons, updateIndirectStipulation } from '../../store/dealerService/actions';
import { AppState } from '../../store/rootReducer';
import { ContextMenu, MenuSelectEvent } from '@progress/kendo-react-layout';
import { IApplication } from '../../Interfaces/Application/IApplication';
import { Constants } from '../../utils/constants';
import { IAppCustomer } from '../../Interfaces/Application/IAppCustomer';
import { Document, uploadFile } from '../../Data/ApplicationDocumentData';
import { GridCellProps, GridRowProps } from '@progress/kendo-react-grid';
import { ApplicationCustomerStipulationDocument } from '../../Data/IndirectStipulationDocument';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import IndirectStipulationDocumentDialog from '../Dialogs/IndirectStipulationDocumentDialog/IndirectStipulationDocumentDialog';
import { useApplicationCustomerStipulations } from '../../Data/StipulationData';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, CheckboxChangeEvent, TextBox } from '@progress/kendo-react-inputs';
import { CreateIndirectDecisionDenialReason } from '../../Data/CreateIndirectDecisionDenialReason';
import { IStipulationCategory } from '../../Interfaces/StipulationTypes';
import { IApplicationDocument } from '../../Interfaces/Application/IApplicationDocument';
import { queryCache } from 'react-query';
import { fetchFile } from '../../Data/fetch';
import { SvgIcon } from '@progress/kendo-react-common';
import { filePdfIcon, pencilIcon, trashIcon } from '@progress/kendo-svg-icons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { HtmlTooltip } from '../Container/Dashboard/Dashboard';
import { useDialog } from '../../providers/DialogProvider';
import ConfirmationDialog from '../Dialogs/ConfirmationDialog';
import { authService } from '../..';
import { httpPostAsync } from '../../utils/HttpClient';

export interface ImageViewerDialogData {
  documentName?: string; // name of the document
  objectUrl?: string; // URL to the image to display
  open: boolean; // whether the dialog is open
}

export interface IndirectStipsEditorProps {
  documents: Document[];
}

// Custom RowItem for the Denial Reasons grid. We want to show an "Delete" button for each item
// When the user clicks the button, we will open the ConfirmDialog to confirm the deletion
const DenialReasonRowItem = (props: GridRowProps & { onDelete: (itemId: string) => void }) => {
  return (
    <tr style={{ display: 'flex', width: '100%' }}>
      <td style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        {props.dataItem?.name}
        <Button onClick={() => props.onDelete(props.dataItem.id)} themeColor='error' fillMode={'flat'} title='Delete Denial Reason'>
          <SvgIcon icon={trashIcon} color='error' />
        </Button>
      </td>
    </tr>
  );
}

// returns object URL for the file. Uses preSignedUrl if available, otherwise downloads from API.
const downloadFile = async (documentId: string, dispatch: any, preSignedUrl?: string) => {
  if (preSignedUrl) {
    return preSignedUrl;
  }
  const url = `api/applicationdocument/download/${documentId}`;
  const response = await fetchFile(url, dispatch);
  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
}

function IndirectStipsEditorInner(props: IndirectStipsEditorProps) {
  const { documents } = props;
  const dispatch = useDispatch();
  const [openDialog, closeDialog] = useDialog();
  const application: IApplication | undefined = useSelector((state: AppState) => state.applications.editingApp);
  const primaryCustomer: IAppCustomer | undefined = application?.appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId.toUpperCase());
  const coApplicant: IAppCustomer | undefined = application?.appCustomers.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleCoApplicant1Id.toUpperCase());
  const { data: stipulationData } = useApplicationCustomerStipulations(application?.id || '');
  const dealerServiceAppHistoryItems = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  const dealerServiceDecisionReasonCodes = useSelector((state: AppState) => state.applications.lookups.dealerServiceDecisionReasonCodes);
  const applicationStipulations = useSelector((state: AppState) => state.dealerService.applicationStipulations);
  const adverseActionReasons = useSelector((state: AppState) => state.dealerService.adverseActionReasons);
  const applicationStipulationStatuses = useSelector((state: AppState) => state.applications.lookups.applicationStipulationStatuses);
  const indirectStipulationDynamicTextDictionary = useSelector((state: AppState) => state.dealerService.indirectStipulationDynamicTextDictionary);
  const stipulationCategories = useSelector((state: AppState) => state.applications.stipulationCategories);

  // Track which document IDs we've already processed URLs for, so we don't re-download on every poll cycle
  const processedDocIds = useRef<Set<string>>(new Set());
  // List of DocumentId/ObjectUrl pairs for each document
  const [documentUrls, setDocumentUrls] = useState<{ documentId: string, objectUrl: string | undefined, isLoading: boolean }[]>([]);

  // Stable key derived from document IDs — only changes when docs are actually added/removed
  const documentIdKey = useMemo(
    () => documents.map(d => d.documentId).sort().join(','),
    [documents]
  );


  // ID of the indirectStip to associate documents with. If a value exists, we will show the IndirectStipulationDocumentDialog window
  const [indirectStipId, setIndirectStipId] = useState<string | undefined>(undefined);  // whether or not to show the image viewer dialog
  // ID of the indirectStip the user is editing
  const [indirectStipIdToEdit, setIndirectStipIdToEdit] = useState<string | undefined>(undefined);
  // StatusId/Notes for the indirectStip the user is editing
  const [editedStipInfo, setEditedStipInfo] = useState<{ statusId: string, notes: string }>({ statusId: '', notes: '' });
  // Index of the step for the IndirectStipulationDocumentDialog
  const [indirectStipDocumentDialogStepIndex, setIndirectStipDocumentDialogStepIndex] = useState(0);
  // Id of the selected document to associate with stipulations
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  // Ids of the selected stipulations to associate the document with
  const [selectedStipulationIds, setSelectedStipulationIds] = useState<number[]>([]);
  // whether or not to show the image viewer dialog (for previewing images in full size)
  const [showImageViewerDialog, setShowImageViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // whether or not to show the pdf viewer dialog (for previewing PDFs in full size)
  const [showPdfViewerDialog, setShowPdfViewerDialog] = useState<ImageViewerDialogData>({ open: false });
  // Whether or not we are uploading a document the user has dropped into the dropzone
  const [isUploadingDocument, startFileUpload] = useTransition();
  // Whether or not to show the "Add Denial Reason" window
  const [showAddDenialReasonWindow, setShowAddDenialReasonWindow] = useState(false);
  // List of dealerServiceDecisionReasonCode IDs user has selected to associate with the decision
  const [selectedDenialReasonIds, setSelectedDenialReasonIds] = useState<string[]>([]);
  // Whether or not we are creating new denial reasons
  const [isCreatingDenialReasons, startCreatingDenialReasons] = useTransition();
  // Denial reason search text
  const [denialReasonSearchText, setDenialReasonSearchText] = useState('');
  // ID of the denial reason we are prompting the user to delete
  const [denialReasonToDeleteId, setDenialReasonToDeleteId] = useState<string | undefined>(undefined);

  const [applicantSelected, setApplicantSelected] = useState(false);
  const [coApplicantSelected, setCoApplicantSelected] = useState(false);
  const [showContextMenu, setShowContextMenu] = React.useState(false);
  const [contextMenuOffset, setContextMenuOffset] = React.useState({ left: 0, top: 0 });
  const [contextTarget, setContextTarget] = useState<{ stipulationId: number; applicationStipulationId: string; appCustomerDocumentStipulationId: string } | null>(null);


  // Files were dropped into the dropzone.
  const onDropAccepted = async (files: File[]) => {
    startFileUpload(() => {
      files.forEach(f => {
        // We cannot use "async => then" until we upgrade to React 19
        uploadFile(application!.id, f).then((ad: IApplicationDocument) => {
        }).catch((reason: Error) => {
          dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: reason.message, isError: true } });
        }).finally(() => {
          // refetch "applicationDocuments" query
          queryCache.refetchQueries(["applicationDocuments", application!.id]);
        });
      });
    });
  };


  const onApplicantChecked = async (checked: boolean) => {
    setApplicantSelected(checked);
  };

  const onCoApplicantChecked = async (checked: boolean) => {
    setCoApplicantSelected(checked);
  };


  useEffect(() => {
    if (application ) {
      dispatch(loadIndirectStipsAndDecisionReasons(application.id))
    }
  }, [application, dealerServiceAppHistoryItems]);

  // Async POST function to download all the document bytes via the applicationDocument/downloadDocuments API. AFter the server returns a result,
 // add them to the documentUrls array. This will replace the useEffect above.
 // The servers returns the following JSON. DocumentId, and byte array
 // { documentId: string, bytes: string }
 const downloadAllFiles = async (documentIds: string[]) => {
   // get token
   const bearerToken = await authService.getToken();
   const documentIdBytes: Response | undefined = await httpPostAsync(`api/applicationDocument/downloadDocuments`,
     documentIds,
     bearerToken);
   if (documentIdBytes && documentIdBytes.ok) {
     const documentIdByteArray: { documentId: string, bytes: string }[] = await documentIdBytes.json();
     setDocumentUrls(prev => {
       const updated = [...prev];
       documentIdByteArray.forEach(docIdBytes => {
         // Convert the byte array to a blob
         const byteCharacters = atob(docIdBytes.bytes);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
           byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray]);
         const objectUrl = window.URL.createObjectURL(blob);
         // update the item if it exists in the array. Otherwise, add it.
         const index = updated.findIndex(du => du.documentId.toUpperCase() === docIdBytes.documentId.toUpperCase());
         if (index !== -1) {
           updated[index] = { documentId: docIdBytes.documentId, objectUrl: objectUrl || undefined, isLoading: false };
         } else {
           updated.push({ documentId: docIdBytes.documentId, objectUrl: objectUrl || undefined, isLoading: false });
         }
       });
       return updated;
     });
   } else {
     // set isLoading to false for all items in the documentUrls array
     setDocumentUrls(prev => prev.map(du => ({ ...du, isLoading: false })));
      // Show error message and result code
      dispatch({ 
        type: "SET_SERVER_MESSAGE", 
        payload: { 
          message: `Error downloading documents (IDs: ${documentIds.join(', ')}): ${documentIdBytes?.statusText || 'Unknown error'}. Please try again or contact support if the problem persists.`, 
          isError: true 
        } 
      });
   }
 }

  // Process URLs only for genuinely new documents (not already processed).
  // Depends on documentIdKey (stable string of sorted IDs) so it only fires when docs are added/removed, not on every 15s poll.
  useEffect(() => {
    const newDocs = documents.filter(doc => !processedDocIds.current.has(doc.documentId.toUpperCase()));
    if (newDocs.length === 0) return;

    // Mark these as processed immediately so we don't re-process on next render
    newDocs.forEach(doc => processedDocIds.current.add(doc.documentId.toUpperCase()));

    const docsNeedingDownload: string[] = [];
    const newUrlEntries: { documentId: string, objectUrl: string | undefined, isLoading: boolean }[] = [];

    newDocs.forEach(doc => {
      if (doc.preSignedUrl) {
        newUrlEntries.push({ documentId: doc.documentId, objectUrl: doc.preSignedUrl, isLoading: false });
      } else {
        newUrlEntries.push({ documentId: doc.documentId, objectUrl: undefined, isLoading: true });
        docsNeedingDownload.push(doc.documentId);
      }
    });

    setDocumentUrls(prev => [...prev, ...newUrlEntries]);

    if (docsNeedingDownload.length > 0) {
      downloadAllFiles(docsNeedingDownload);
    }
  }, [documentIdKey]);


  const stipulationDocumentTypes = stipulationCategories?.flatMap(sc => sc.stipulations);
  // Returns the filtered list of indirect stipulations for the selected customer, sorted by sequence number
  const getStipulations = () => {
    return applicationStipulations
      ?.sort((stip1: ApplicationStipulation, stip2: ApplicationStipulation) => stip1.sequenceNumber < stip2.sequenceNumber ? 0 : 1)
      .map(stip => {
        return {
          ...stip,
          statusName: applicationStipulationStatuses.find(status => status.id.toUpperCase() === stip.statusId?.toUpperCase())?.name,
          numberOfRequiredDocs: stipulationData?.stipulationCategoryTypes.flatMap(cat   => cat.stipulationCategories)
              .find(sc => sc.id === stip.stipulationCategoryId)?.numberRequiredForSubmission,
          numberOfRequiredDocsCoApp: stipulationData?.stipulationCategoryTypes.flatMap(cat => cat.stipulationCategories)
              .find(sc => sc.id === stip.stipulationCategoryId)?.numberRequiredForCoApplicantSubmission === null ? 0
                : stipulationData?.stipulationCategoryTypes.flatMap(cat => cat.stipulationCategories)
                  .find(sc => sc.id === stip.stipulationCategoryId)?.numberRequiredForCoApplicantSubmission,
          dynamicText: indirectStipulationDynamicTextDictionary[stip.stipulationCategoryId] ?? stip.dynamicText
        }
      });
  }

  // Edit Cell for the Indirect Stips grid
  const EditIndirectStipulationCell = (props: GridCellProps & { onEdit: (itemId: string) => void }) => {
    return (
      <td>
        <Button onClick={() => props.onEdit(props.dataItem.id)} themeColor='primary' fillMode={'flat'} title='Edit Stipulation'>
          <SvgIcon icon={pencilIcon} color='primary' size='small' />
        </Button>
      </td>
    );
  }

  const DocumentPdfIcon = (props: { documentName: string, documentUrl: string, stipulationId: number, applicationStipulationId: string, appCustomerDocumentStipulationId: string }) => {
    const { documentName, documentUrl, stipulationId, applicationStipulationId, appCustomerDocumentStipulationId } = props;
    return (
      <div onContextMenu={(e) => handleIconContextMenu(e, { stipulationId, applicationStipulationId, appCustomerDocumentStipulationId })}>
        <SvgIcon
          icon={filePdfIcon}
          onClick={() => setShowPdfViewerDialog({
            documentName: documentName,
            objectUrl: documentUrl || '',
            open: true
          })}
          height={30}
          color='red'
          style={{ cursor: 'pointer' }}
        />
      </div>
    );
  }
  const DocumentIcon = (props: { documentName: string, documentUrl: string, stipulationId: number, applicationStipulationId: string, appCustomerDocumentStipulationId: string }) => {
    const { documentName, documentUrl, stipulationId, applicationStipulationId, appCustomerDocumentStipulationId } = props;
    return (
      <div onContextMenu={(e) => handleIconContextMenu(e, { stipulationId, applicationStipulationId, appCustomerDocumentStipulationId })} style={{display: 'flex', flex: 0, alignItems: 'center'}}>
         <img
            src={documentUrl}
            alt={`${documentName} - ${stipulationDocumentTypes?.find(sdt => sdt.id === stipulationId)?.name ?? ""}`}
            onClick={() => setShowImageViewerDialog({
              documentName: documentName,
              objectUrl: documentUrl || '',
              open: true
            })}
            height={40}
            style={{ cursor: 'pointer' }}
          />
      </div>
    );
  }


  const NameWithImagesCell = (cellProps: GridCellProps) => {
    const dataItem: ApplicationStipulation = cellProps.dataItem;
    // const documentsToShow = documents.filter(doc => dataItem.applicationCustomerStipulationDocuments.some((isd: ApplicationStipulationDocument) => isd.documentId.toUpperCase() === doc.documentId.toUpperCase()));
    const documentsWithAppCustomerStipulation: {document: Document, stipulationInfo: {appCustomerStipulationDocumentId: string, stipulationId: number}}[] = [];
    dataItem.applicationCustomerStipulationDocuments?.forEach((appCustomerStipulationDocument: ApplicationCustomerStipulationDocument) => {
      const document = documents.find(d => d.documentId.toUpperCase() === appCustomerStipulationDocument.documentId.toUpperCase());
      if (document) {
        documentsWithAppCustomerStipulation.push(
          {
            document,
            stipulationInfo: { appCustomerStipulationDocumentId: appCustomerStipulationDocument.id, stipulationId: appCustomerStipulationDocument.stipulationId }
          });
      }
    });

    return (
      <td>
        <div className={styles.nameWithDocuments}>
          <span className={styles.itemName}>{dataItem.dynamicText && hasValue(dataItem.dynamicText) ? dataItem.dynamicText : dataItem.name}</span>
          <div className={styles.documentImages}>
            {documentsWithAppCustomerStipulation.map(documentWithAppCustomerStipulation => {
              const documentUrlItem = documentUrls.find(du => du.documentId.toUpperCase() === documentWithAppCustomerStipulation.document.documentId.toUpperCase());
              const documentUrl = documentUrlItem?.objectUrl || documentWithAppCustomerStipulation.document.preSignedUrl;
              return (
                <HtmlTooltip
                arrow
                title={
                  <React.Fragment>
                    <p style={{ margin: 0, padding: 0 }}>
                      {documentWithAppCustomerStipulation.document.name + (documentWithAppCustomerStipulation.document.fileExtension ? documentWithAppCustomerStipulation.document.fileExtension : '')}
                      <br />
                      {stipulationDocumentTypes?.find(sdt => sdt.id === documentWithAppCustomerStipulation.stipulationInfo.stipulationId)?.name ?? ""}
                    </p>
                  </React.Fragment>
                }
              >
                  <div className={styles.documentIcon} style={{marginRight: '5px'}} >
                    {(documentWithAppCustomerStipulation.document.mimeType !== 'application/pdf' && documentUrl) && (
                     <DocumentIcon documentName={documentWithAppCustomerStipulation.document.name}
                      documentUrl={documentUrl}
                      appCustomerDocumentStipulationId={documentWithAppCustomerStipulation.stipulationInfo.appCustomerStipulationDocumentId}
                      stipulationId={documentWithAppCustomerStipulation.stipulationInfo.stipulationId}
                      applicationStipulationId={cellProps.dataItem.id}
                     />
                    )}
                    {(documentWithAppCustomerStipulation.document.mimeType === 'application/pdf' && documentUrl) && (
                      <DocumentPdfIcon documentName={documentWithAppCustomerStipulation.document.name}
                        documentUrl={documentUrl}
                        appCustomerDocumentStipulationId={documentWithAppCustomerStipulation.stipulationInfo.appCustomerStipulationDocumentId}
                        stipulationId={documentWithAppCustomerStipulation.stipulationInfo.stipulationId}
                        applicationStipulationId={cellProps.dataItem.id}
                      />
                    )}
                  </div>
                </HtmlTooltip>

              );
            })}

          </div>
        </div>
      </td>
    );
  };

  // returns the dialog title, which is the document name. truncates the document name if it is too long (20 characters)
  const getDialogTitle = (documentName: string | undefined): string => {
    return documentName ? documentName.length > 20 ? documentName.substring(0, 20) + '...' : documentName : '';
  }

  // DecisionStipsGridHeaderComponent now uses primaryCustomer.id directly instead of checking selectedTabIndex
  // If any document is loading, show small loading indicator next to the title
  const DecisionStipsGridHeaderComponent: React.FC = (props: any) => {
    return (
      <div style={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '2em' }}>
          Application Stips
          {documentUrls.some(du => du.isLoading) && (
            <LoadingPanel label='Loading documents...' />
          )}
        </div>
        <Button
          onClick={() => setIndirectStipId(getStipulations()![0].id)}
          disabled={((getStipulations()?.length || 0) === 0) || getStipulations()?.every(stip => stip.numberOfRequiredDocs === 0)}
          themeColor='primary'>
          Associate Document with Stips
        </Button>
      </div>
    );
  };

  const createDenialReasons = () => {
    setShowAddDenialReasonWindow(false);
    startCreatingDenialReasons(() => {
      // Create the array of CreateIndirectDecisionDenialReason objects. Sequence number will begin with indirectDecisionReasons.length, and increment by 1 for each selectedDenialReasons. Order by selectedDenialReason.sortOrder
      const startSequenceNumber = adverseActionReasons ? adverseActionReasons.length : 0;
      // Sort the selected denial reasons by sortOrder so we can assign sequence numbers in proper order
      const sortedDenialReasons = dealerServiceDecisionReasonCodes.filter(reason => selectedDenialReasonIds.includes(reason.id)).sort((reason1, reason2) => (reason1.sortOrder || 0) - (reason2.sortOrder || 0));
      const createDenialReasons: CreateIndirectDecisionDenialReason[] = sortedDenialReasons.map((reason, index) => ({
        dealerServiceDecisionReasonCodeId: reason.id,
        applicationId: application!.id,
        sequenceNumber: startSequenceNumber + index
      }));
      // Dispatch the action to create the denial reasons
      dispatch(createDecisionDenialReasons({ createDenialReasons }));
      // clear the selected denial reasons
      setSelectedDenialReasonIds([]);
    });
  }

  // Returns the list of stipulation categories to show in the Associate Document with Stipulation dialog.
  // We only show categories that are in the list of indiredctStipulations for the selected customer, and that require at least one document.
  const getStipulationCategoriesForDocumentDialog = () => {
    const stipulationCategoriesForDialog: IStipulationCategory[] = [];
    // Loop over each indirect stipulation for the selected customer. Find the category for each stipulation, and add it to the list if it doesn't already exist.
    // We do this because the stipulationData.stipulationCategoryTypes list has the stipulations for each category.
    applicationStipulations?.forEach(stip => {
      const category = stipulationData?.stipulationCategoryTypes.find(cat => cat.stipulationCategories.some(sc => sc.id === stip.stipulationCategoryId));
      if (category) {
        const stipCategory = category.stipulationCategories.find(sc => sc.id === stip.stipulationCategoryId);
        // Prevent adding duplicates to list
        if (stipCategory && !stipulationCategoriesForDialog.some(sc => sc.id === stipCategory.id) && stipCategory.numberRequiredForSubmission > 0) {
          stipulationCategoriesForDialog.push(stipCategory);
        }
      }
    });
    return stipulationCategoriesForDialog;
  }



  // Handles the association of a document with stipulations. Dispatches the action to associate the document with the selected stipulations, then closes the dialog.
  const handleAssociatingDocumentWithStipulations = async () => {

    const appCustomerIds: string[] = [];

    if (applicantSelected && primaryCustomer?.id) {
      appCustomerIds.push(primaryCustomer.id);
    }

    if (coApplicantSelected && coApplicant?.id) {
      appCustomerIds.push(coApplicant.id);
    }

    dispatch(associateDocumentWithStipulations({
      documentId: selectedDocumentId,
      stipulationIds: selectedStipulationIds,
      appCustomerIds: appCustomerIds,
    }, stipulationData?.stipulationCategoryTypes.flatMap(cat => cat.stipulationCategories) ?? []));

    // Reset the dialog state
    setApplicantSelected(false);
    setCoApplicantSelected(false);
    setIndirectStipId(undefined);
    setSelectedDocumentId('');
    setSelectedStipulationIds([]);
    setIndirectStipDocumentDialogStepIndex(0);
    queryCache.refetchQueries(["ApplicationCustomerStipulations", application!.id]);
    queryCache.refetchQueries(["ApplicationDocuments", application!.id]);
  }


  const onDeleteDenialReason = (denialReasonId: string) => {
    dispatch(deleteDecisionDenialReason(denialReasonId));
    setDenialReasonToDeleteId(undefined);
  }


  const gridColumns = [
    {
      title: '', cell: (props: GridCellProps) => (
        <EditIndirectStipulationCell {...props}
          onEdit={(itemId: string) => {
            setEditedStipInfo({ statusId: applicationStipulations?.find(stip => stip.id === itemId)?.statusId || '', notes: applicationStipulations?.find(stip => stip.id === itemId)?.note || '' });
            setIndirectStipIdToEdit(itemId);
          }} />
      ), width: '60px'
    },
    { field: 'name', title: 'Name', cell: NameWithImagesCell },
    { field: 'type', title: 'Type', width: '120px' },
    { field: 'statusName', title: 'Status', width: '150px' },
    { field: 'note', title: 'Notes', width: '200px' },
    { field: 'numberOfRequiredDocs', title: '# Required', width: '170px' },
  ];

  // Only add the CoApplicant Docs column if coApplicant exists
  if (coApplicant) {
    gridColumns.push({ field: 'numberOfRequiredDocsCoApp', title: '# CoApplicant Docs', width: '190px' });
  }

  const contextMenuItems = [
    {
      text: 'Remove Document', action: (item: any, stipulationId: number, applicationStipulationId: string, appCustomerStipulationDocumentId: string) => {
        openDialog({
          fullScreen: true,
          children: (
            <ConfirmationDialog
              closeDialog={closeDialog}
              prompt={`Are you sure you want to remove this document from the ${stipulationCategories?.find(sc => sc.stipulations.some(s => s.id === stipulationId))?.stipulations.find(s => s.id === stipulationId)?.name} stipulation?`}
              title="Remove Stipulation Document?"
              accept={async () => {
                dispatch(deleteAppCustomerStipDocumentAssociation(applicationStipulationId, appCustomerStipulationDocumentId));
                closeDialog();
              }}
            />
          )
        });
      }
    },
  ];

  const handleIconContextMenu = (event: React.MouseEvent, target: { stipulationId: number; applicationStipulationId: string; appCustomerDocumentStipulationId: string }) => {
    event.preventDefault();
    event.stopPropagation();
    setContextTarget(target);
    setContextMenuOffset({ left: event.clientX, top: event.clientY });
    setShowContextMenu(true);
  };

  const handleCloseContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleContextMenuSelect = (event: MenuSelectEvent) => {
    const item = contextMenuItems.find(i => i.text === event.item.text);
    if (item && item.action && contextTarget) {
      item.action(event, contextTarget.stipulationId, contextTarget.applicationStipulationId, contextTarget.appCustomerDocumentStipulationId);
    }
    setShowContextMenu(false);
  };

  return (
    <div className={styles.indirectStipsEditorContainer} >
      <div className={styles.gridContainer}>
        <GenericGrid<ApplicationStipulation>
          // itemName={'Application Stips'}
          style={{ flex: 1, minHeight: 'calc(60vh - 146px)', maxHeight: 'calc(50vh - 146px)', overflowY: 'auto' }}
          itemNameStyle={{ fontWeight: 'bold', textWrap: 'nowrap', fontSize: '15px' }}
          items={getStipulations() || []}
          disableSorting={true}
          disableFiltering={true}
          disablePaging={true}
          disableGrouping={true}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableAdding={true}
          className={styles.kendoGrid}
          disableEditing={true}
          headerComponent={<DecisionStipsGridHeaderComponent />}
          columns={gridColumns}
        />

      </div>

      <div className={styles.denialReasonsContainer}>
        <GenericGrid<IndirectDecisionReason>
          size={'small'}
          style={{ flex: 1, minHeight: 'calc(50vh - 146px)' }}
          itemName={'Adverse Action Reasons'}
          itemNameStyle={{ fontWeight: 'bold', textWrap: 'nowrap', fontSize: '15px' }}
          rowRender={(row: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => <DenialReasonRowItem {...props} onDelete={(itemId: string) => setDenialReasonToDeleteId(itemId)} />}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableEditing={true}
          items={adverseActionReasons?.sort((reason1: IndirectDecisionReason, reason2: IndirectDecisionReason) => reason1.sequenceNumber < reason2.sequenceNumber ? 0 : 1) || []}
          disableSorting={true}
          disableFiltering={true}
          disablePaging={true}
          disableGrouping={true}
          disableAdding={isCreatingDenialReasons}
          className={styles.kendoGrid}
          onOpenItemEditor={(item?) => {
            setShowAddDenialReasonWindow(true);
          }}
          columns={[
            { field: 'name', title: 'Name' },
          ]}
        />
      </div>

      {showImageViewerDialog.open && (
        <Window title={getDialogTitle(showImageViewerDialog.documentName)} onClose={() => setShowImageViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })} height={500} width={500}>
          <img src={showImageViewerDialog.objectUrl} alt={showImageViewerDialog.documentName} style={{ width: '100%', height: '100%', overflow: 'auto' }} />
        </Window>
      )
      }
      {
        showPdfViewerDialog.open && (
          <Window title={getDialogTitle(showPdfViewerDialog.documentName)} onClose={() => setShowPdfViewerDialog({ documentName: undefined, objectUrl: undefined, open: false })} stage='FULLSCREEN'>
            <iframe src={showPdfViewerDialog.objectUrl} title={showPdfViewerDialog.documentName} style={{ width: '100%', height: '100%' }} />
          </Window>
        )
      }
      {
        hasValue(indirectStipId) && (
          <Window title="Associate Document with Stipulation" onClose={() => setIndirectStipId(undefined)} themeColor='primary' height={700} width={600}>
            <IndirectStipulationDocumentDialog
              documents={documents}
              documentUrls={documentUrls}
              selectedDocumentId={selectedDocumentId}
              selectedStipulationIds={selectedStipulationIds}
              stipulationCategories={getStipulationCategoriesForDocumentDialog()}
              stepIndex={indirectStipDocumentDialogStepIndex}
              applicationStipulation={applicationStipulations!.find(stip => stip.id === indirectStipId)!}
              isUploadingDocument={isUploadingDocument}
              onSelectStipulation={(stipulationId) => {
                if (selectedStipulationIds.includes(stipulationId)) {
                  setSelectedStipulationIds(selectedStipulationIds.filter(id => id !== stipulationId));
                } else {
                  setSelectedStipulationIds([...selectedStipulationIds, stipulationId]);
                }
              }}
              onSelectDocument={(documentId) => {
                setSelectedDocumentId(documentId);
                // Download the file and store the URL in the documentUrls array if it doesn't already exist
                const doc = documents.find(d => d.documentId === documentId);
                downloadFile(documentId, dispatch, doc?.preSignedUrl).then(objectUrl => {
                  setDocumentUrls(prev => {
                    const updated = [...prev];
                    const index = updated.findIndex(du => du.documentId.toUpperCase() === documentId.toUpperCase());
                    if (index !== -1) {
                      updated[index] = { documentId, objectUrl: objectUrl || undefined, isLoading: false };
                    } else {
                      updated.push({ documentId, objectUrl: objectUrl || undefined, isLoading: false });
                    }
                    return updated;
                  });
                });
                // If the user has already selected the document, we need to pre-select the stipulations that are already associated with the document
                const document = documents.find(doc => doc.documentId === documentId);
                if (document) {
                  const stipIds = applicationStipulations!.filter(stip => stip.applicationCustomerStipulationDocuments?.some(isd => isd.documentId.toUpperCase() === documentId.toUpperCase())).flatMap(stip => stip.applicationCustomerStipulationDocuments.filter(isd => isd.documentId.toUpperCase() === documentId.toUpperCase()).map(isd => isd.stipulationId));
                  setSelectedStipulationIds(stipIds);
                }
              }}
              onUploadFiles={onDropAccepted}
              onApplicantChecked={onApplicantChecked}
              onCoApplicantChecked={onCoApplicantChecked}
            />
            <WindowActionsBar>
              <Button onClick={() => {
                setSelectedDocumentId('');
                setSelectedStipulationIds([]);
                setIndirectStipDocumentDialogStepIndex(0);
                setIndirectStipId(undefined);
              }}
                themeColor='base'
                disabled={isUploadingDocument}>
                Cancel
              </Button>
              {indirectStipDocumentDialogStepIndex === 0 && (
              <Button
                onClick={() => setIndirectStipDocumentDialogStepIndex(1)}
                themeColor='primary'
                disabled={isUploadingDocument || !hasValue(selectedDocumentId) || !(applicantSelected || coApplicantSelected)}>
                Next
              </Button>
              )}
              {indirectStipDocumentDialogStepIndex === 1 && (
                <>
                  <Button onClick={() => setIndirectStipDocumentDialogStepIndex(0)} themeColor='base' disabled={isUploadingDocument}>Back</Button>
                  <Button onClick={() => handleAssociatingDocumentWithStipulations()} themeColor='primary' disabled={isUploadingDocument}>Save</Button>
                </>
              )}

            </WindowActionsBar>
          </Window>
        )
      }
      {
        hasValue(indirectStipIdToEdit) && (
          <Window title="Change Stipulation Status" onClose={() => setIndirectStipIdToEdit(undefined)} themeColor='primary' height={270} width={350}>
            <DropDownList
              data={applicationStipulationStatuses.filter(status => status.id !== applicationStipulations?.find(stip => stip.statusId?.toUpperCase() === indirectStipIdToEdit)?.statusId?.toUpperCase())}
              textField='name'
              dataItemKey='id'
              value={applicationStipulationStatuses.find(status => status.id.toUpperCase() === editedStipInfo.statusId?.toUpperCase())}
              onChange={(e: DropDownListChangeEvent) => setEditedStipInfo({ ...editedStipInfo, statusId: e.target.value.id as string })}
              label='Status'
              style={{ marginBottom: '1em', width: '100%' }}
            />
            <label style={{ color: '#757575' }}>Notes</label>
            <TextBox
              value={editedStipInfo.notes}
              onChange={(e) => setEditedStipInfo({ ...editedStipInfo, notes: e.target.value as string })}
              placeholder='Add notes...'
              style={{ marginBottom: '1em' }}
            />
            <WindowActionsBar>
              <Button onClick={() => {
                setEditedStipInfo({ statusId: '', notes: '' });
                setIndirectStipIdToEdit(undefined);
              }} themeColor='base'>
                Cancel
              </Button>
              <Button
                disabled={!editedStipInfo.statusId}
                onClick={() => {
                  // Dispatch the action to update the stipulation
                  dispatch(updateIndirectStipulation(indirectStipIdToEdit!, editedStipInfo.statusId, editedStipInfo.notes));
                  setIndirectStipIdToEdit(undefined);
                }} themeColor='primary'>
                Save
              </Button>
            </WindowActionsBar>
          </Window>
        )
      }
      {
        showAddDenialReasonWindow && (
          <Window title="Add Adverse Action Reasons" onClose={() => setShowAddDenialReasonWindow(false)} themeColor='primary' height={600} width={600}>
            <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              <TextBox
                value={denialReasonSearchText}
                onChange={(e) => setDenialReasonSearchText(e.target.value as string)}
                type='search'
                placeholder="Search denial reasons..."
                style={{ marginBottom: '1em' }}
              />
              {
                dealerServiceDecisionReasonCodes
                  .filter(reason => !adverseActionReasons.some(dr => dr.dealerServiceDecisionReasonCodeId.toUpperCase() === reason.id.toUpperCase()) && reason.description.toUpperCase().includes(denialReasonSearchText.toUpperCase()))
                  .sort((reason1, reason2) => (reason1.sortOrder || 0) - (reason2.sortOrder || 0))
                  .map(reason => (
                    <Checkbox
                      key={reason.id}
                      label={reason.description}
                      checked={selectedDenialReasonIds.includes(reason.id)}
                      onChange={(e: CheckboxChangeEvent) => {
                        if (e.value) {
                          setSelectedDenialReasonIds([...selectedDenialReasonIds, reason.id]);
                        } else {
                          setSelectedDenialReasonIds(selectedDenialReasonIds.filter(dr => dr !== reason.id));
                        }
                      }}
                      style={{ cursor: 'pointer', margin: '5px 0' }}
                    />
                  ))
              }
              {hasValue(denialReasonSearchText) && dealerServiceDecisionReasonCodes.filter(reason => !adverseActionReasons.some((dr: IndirectDecisionReason) => dr.dealerServiceDecisionReasonCodeId.toUpperCase() === reason.id.toUpperCase()) && reason.description.toUpperCase().includes(denialReasonSearchText.toUpperCase())).length === 0 && (
                <p style={{ margin: 0 }}>No denial reasons found for your search.</p>
              )}
            </div>
            <WindowActionsBar>
              <Button
                onClick={() => {
                  setSelectedDenialReasonIds([]);
                  setShowAddDenialReasonWindow(false);
                }}
                themeColor='base'
              >
                Cancel
              </Button>
              <Button onClick={() => createDenialReasons()} themeColor='success' disabled={isCreatingDenialReasons || selectedDenialReasonIds.length === 0}>
                Add
              </Button>
            </WindowActionsBar>
          </Window>
        )
      }
      {
        hasValue(denialReasonToDeleteId) && (
          <Window title="Delete Denial Reason" onClose={() => setDenialReasonToDeleteId(undefined)} themeColor='primary' height={200} width={400}>
            <p style={{ margin: 0 }}>Are you sure you want to delete the denial reason: <b>{adverseActionReasons.find(dr => dr.id === denialReasonToDeleteId)?.name}</b>?</p>
            <WindowActionsBar>
              <Button onClick={() => setDenialReasonToDeleteId(undefined)} themeColor='base'>
                Cancel
              </Button>
              <Button onClick={() => onDeleteDenialReason(denialReasonToDeleteId!)}
                themeColor='error'>
                Delete
              </Button>
            </WindowActionsBar>
          </Window>
        )
      }
      {/* Global Context Menu for document actions */}
      <ContextMenu
        items={contextMenuItems}
        show={showContextMenu}
        offset={contextMenuOffset}
        onClose={handleCloseContextMenu}
        onSelect={handleContextMenuSelect}
      />
    </div >
  );
}

// Only re-render when the actual set of document IDs changes, not on every 15s poll reference change
function arePropsEqual(prev: IndirectStipsEditorProps, next: IndirectStipsEditorProps): boolean {
  if (prev.documents.length !== next.documents.length) return false;
  const prevIds = new Set(prev.documents.map(d => d.documentId));
  return next.documents.every(d => prevIds.has(d.documentId));
}

export default React.memo(IndirectStipsEditorInner, arePropsEqual);
